import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Loader2, RotateCcw, Save } from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  getTtpaAutodraftTemplate,
  saveTtpaAutodraftTemplate,
} from "@/lib/ttpa.functions";
import {
  TTPA_AUTODRAFT_PLACEHOLDERS,
  renderTtpaTemplate,
} from "@/lib/ttpa-template";

export const Route = createFileRoute("/_admin/admin/ttpa/vorlage")({
  component: TtpaTemplatePage,
});

const SAMPLE: Record<string, string> = {
  name: "Anna Anders",
  transparenz_email: "transparenz@anna-anders.de",
  anschrift: "Musterstraße 1  \n12345 Musterstadt",
  kontrolleinrichtung: "**Kreisverband Musterstadt**  \nMusterweg 2, 12345 Musterstadt  \nE-Mail: kontakt@kv-musterstadt.de",
  berechtigung_satz:
    "Der Auftraggeber ist Unionsbürgerin oder Unionsbürger und damit berechtigt, politische Werbung in der Europäischen Union in Auftrag zu geben.",
  partei: "Musterpartei",
  rolle: "Kandidatin oder Kandidat",
  amt: "Bürgermeisteramt",
  wahlgebiet: "Stadt Musterstadt",
  website: "https://anna-anders.de",
};

function TtpaTemplatePage() {
  const getFn = useServerFn(getTtpaAutodraftTemplate);
  const saveFn = useServerFn(saveTtpaAutodraftTemplate);
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-ttpa-template"],
    queryFn: () => getFn(),
  });

  const [body, setBody] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (data) setBody(data.bodyMd);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (bodyMd: string) => saveFn({ data: { bodyMd } }),
    onSuccess: () => {
      setNote("Vorlage gespeichert.");
      setSaveError(null);
      qc.invalidateQueries({ queryKey: ["admin-ttpa-template"] });
    },
    onError: (e) => setSaveError(e instanceof Error ? e.message : String(e)),
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Vorlage wird geladen …
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
        {error instanceof Error ? error.message : String(error)}
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <Link
        to="/admin/ttpa"
        className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Zurück zur Übersicht
      </Link>
      <h1 className="mt-2 font-display text-3xl font-extrabold md:text-4xl">
        Vorlage für automatische Entwürfe
      </h1>
      <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
        Aus diesem Rahmentext entsteht der Entwurf, sobald ein Kunde den
        TTPA-Fragebogen abschließt. Platzhalter werden mit den Antworten ersetzt.
        Optionale Blöcke schreiben Sie als{" "}
        <code className="rounded bg-secondary px-1">
          {"{{"}#if anschrift{"}}"} … {"{{"}/if{"}}"}
        </code>{" "}
        – fehlt die Angabe, entfällt der Block vollständig.
      </p>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Rahmentext (Markdown)
          </label>
          <textarea
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
              setNote(null);
            }}
            spellCheck={false}
            rows={22}
            className="mt-2 w-full max-w-full rounded-md border border-border bg-background p-3 font-mono text-xs leading-relaxed"
          />

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              onClick={() => saveMutation.mutate(body)}
              disabled={saveMutation.isPending || body.trim() === ""}
              className="inline-flex items-center gap-2 rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground disabled:opacity-60"
            >
              {saveMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Speichern
            </button>
            <button
              onClick={() => {
                if (!data) return;
                setBody(data.defaultBodyMd);
                setNote("Standardtext eingesetzt – noch nicht gespeichert.");
              }}
              className="inline-flex items-center gap-2 rounded border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Auf Standard zurücksetzen
            </button>
            {note && <span className="text-xs text-muted-foreground">{note}</span>}
          </div>

          {saveError && (
            <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              {saveError}
            </div>
          )}

          <div className="mt-8">
            <h2 className="font-display text-lg font-extrabold">Vorschau mit Beispielangaben</h2>
            <pre className="mt-2 max-w-full overflow-x-auto whitespace-pre-wrap break-words rounded-md border border-border bg-secondary/50 p-3 text-xs">
              {renderTtpaTemplate(body, SAMPLE)}
            </pre>
          </div>
        </div>

        <aside className="min-w-0">
          <h2 className="font-display text-lg font-extrabold">Platzhalter</h2>
          <ul className="mt-2 space-y-2 text-xs text-muted-foreground">
            {TTPA_AUTODRAFT_PLACEHOLDERS.map((p) => (
              <li key={p.name} className="rounded-md border border-border p-2">
                <code className="font-mono text-foreground">
                  {"{{"}
                  {p.name}
                  {"}}"}
                </code>
                <p className="mt-1 break-words">{p.description}</p>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  );
}
